import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Select,
  notify,
} from '@aeris/ui'

import {
  fetchProductCategories,
  setMinimumStockByCategory,
} from '../lib/api/product-categories.ts'
import { ApiError } from '../lib/api.ts'
import { categoryLabel } from '../lib/category-label.ts'

/**
 * O estoque mínimo de uma categoria inteira (F288).
 *
 * O Eduardo dita a regra assim — "tela é 5" —, e o aviso de reposição do
 * fechamento só lista quem tem mínimo. Primeiro a prévia diz quantas peças
 * recebem; sem "trocar", o mínimo acertado à mão numa peça continua valendo.
 */
export const MinimumStockDialog = ({
  onClose,
  onDone,
}: {
  readonly onClose: () => void
  readonly onDone: () => void
}): ReactNode => {
  const [categoryId, setCategoryId] = useState('')
  const [minimum, setMinimum] = useState('5')
  const [overwrite, setOverwrite] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const categories = useQuery({
    queryKey: ['product-categories', 'active'],
    queryFn: async ({ signal }) =>
      await fetchProductCategories('active', signal),
  })

  const input = (dryRun: boolean) => ({
    categoryId,
    minimum: minimum.trim(),
    overwrite,
    dryRun,
  })

  const preview = useMutation({
    mutationFn: async () => await setMinimumStockByCategory(input(true)),
    onSuccess: () => {
      setError(null)
    },
    onError: (failure: unknown) => {
      setError(
        failure instanceof ApiError ? failure.message : 'Não deu para contar.'
      )
    },
  })

  const apply = useMutation({
    mutationFn: async () => await setMinimumStockByCategory(input(false)),
    onSuccess: (result) => {
      notify.success(
        `Estoque mínimo ${minimum.trim()} em ${String(result.updated)} ${result.updated === 1 ? 'peça' : 'peças'}.`
      )
      onDone()
      onClose()
    },
    onError: (failure: unknown) => {
      setError(
        failure instanceof ApiError ? failure.message : 'Não deu para gravar.'
      )
    },
  })

  const ready = categoryId !== '' && /^\d{1,6}$/.test(minimum.trim())
  const reset = (): void => {
    preview.reset()
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Estoque mínimo por categoria"
        description="Põe o mesmo mínimo em todas as peças da categoria, com as subcategorias. O fechamento do dia avisa no WhatsApp o que chegou no mínimo."
      >
        <div className="flex flex-col gap-3">
          <Field label="Categoria">
            {(field) => (
              <Select
                id={field.id}
                value={categoryId}
                onChange={(event) => {
                  setCategoryId(event.target.value)
                  reset()
                }}
              >
                <option value="">Escolha…</option>
                {(categories.data ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {categoryLabel(category)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Mínimo (unidades)">
            {(field) => (
              <Input
                id={field.id}
                inputMode="numeric"
                className="w-28"
                value={minimum}
                onChange={(event) => {
                  setMinimum(event.target.value)
                  reset()
                }}
              />
            )}
          </Field>
          <CheckboxField
            label="Trocar também o mínimo de quem já tem"
            checked={overwrite}
            onCheckedChange={(checked) => {
              setOverwrite(checked === true)
              reset()
            }}
          />
          {preview.data === undefined ? null : (
            <InlineAlert tone="info">
              {preview.data.matched === 0
                ? 'Nenhuma peça muda.'
                : `${String(preview.data.matched)} ${preview.data.matched === 1 ? 'peça recebe' : 'peças recebem'} o mínimo ${minimum.trim()}.`}
            </InlineAlert>
          )}
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          {preview.data === undefined ? (
            <Button
              variant="primary"
              disabled={!ready}
              loading={preview.isPending}
              onClick={() => {
                preview.mutate()
              }}
            >
              Ver quantas
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={preview.data.matched === 0}
              loading={apply.isPending}
              onClick={() => {
                apply.mutate()
              }}
            >
              Aplicar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
